import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Loader2, AlertCircle, Eye, EyeOff, UserPlus, Phone, MapPin } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '@/lib/supabase/client';
import { useTranslation } from '@/lib/i18n';
import { LanguageSelector } from '@/components/shared/LanguageSelector';
import {
  searchCities,
  findCityByLabel,
  resolveTimezone,
  resolveTimezoneDisplay,
  getCityWithTimezone,
  getCallingCodeForCity,
  getPhoneFormatExample,
  cities,
  detectBrowserTimezone,
} from '@/lib/cities';

interface LoginProps {
  onClose?: () => void;
}

export const Login: React.FC<LoginProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { signIn, signUp, loading: authLoading, error: authError, user, profile, profileLoaded, refetchProfile } = useAuth();
  const { t } = useTranslation();

  const isHouseholdInvite = searchParams.get('invite') === 'true';
  const inviteEmail = searchParams.get('email') || '';

  const [mode, setMode] = useState<'login' | 'signup'>(isHouseholdInvite ? 'signup' : 'login');
  const [email, setEmail] = useState(isHouseholdInvite ? inviteEmail : '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [signupName, setSignupName] = useState('');
  const [phoneSms, setPhoneSms] = useState('');
  const [phoneWhatsapp, setPhoneWhatsapp] = useState('');
  const [samePhone, setSamePhone] = useState(true);
  const [citySearch, setCitySearch] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const navigatedRef = useRef(false);
  const cityDropdownRef = useRef<HTMLDivElement>(null);

  const filteredCities = useMemo(() => {
    if (!citySearch) return cities.slice(0, 20);
    return searchCities(citySearch).slice(0, 20);
  }, [citySearch]);

  const callingCode = selectedCity
    ? (getCallingCodeForCity(selectedCity) || '1')
    : '1';
  const phonePlaceholder = getPhoneFormatExample(callingCode);
  const resolvedTz = selectedCity ? resolveTimezone(selectedCity) : '';
  const resolvedTzLabel = resolvedTz ? resolveTimezoneDisplay(resolvedTz) : '';

  const formatPhone = (phone: string): string | null => {
    if (!phone) return null;
    const trimmed = phone.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('+')) {
      const digits = trimmed.slice(1).replace(/\D/g, '');
      return digits ? `+${digits}` : null;
    }
    if (trimmed.startsWith('00')) {
      const digits = trimmed.slice(2).replace(/\D/g, '');
      return digits ? `+${digits}` : null;
    }
    const digits = trimmed.replace(/\D/g, '');
    if (!digits) return null;
    return `+${callingCode}${digits}`;
  };

  const from = (location.state as any)?.from?.pathname || '/';

  useEffect(() => {
    if (navigatedRef.current) return;
    if (loginSuccess && user && profileLoaded && profile) {
      navigatedRef.current = true;
      navigate(from, { replace: true });
      onClose?.();
    }
  }, [loginSuccess, user, profileLoaded, profile, from, navigate, onClose]);

  useEffect(() => {
    if (navigatedRef.current) return;
    if (user && profileLoaded && profile && !loginSuccess) {
      navigatedRef.current = true;
      navigate(from, { replace: true });
    }
  }, [user, profileLoaded, profile, loginSuccess, from, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (mode === 'signup') {
      if (password !== confirmPassword) {
        setError(t('Passwords do not match'));
        return;
      }
      if (password.length < 6) {
        setError(t('Password must be at least 6 characters'));
        return;
      }
      setLoading(true);
      const result = await signUp(email, password, signupName || email.split('@')[0]);
      if (!result.success) {
        setError(result.error || t('Sign up failed'));
        setLoading(false);
        return;
      }

      let profileFound = false;
      let delay = 200;
      for (let i = 0; i < 8; i++) {
        await new Promise(r => setTimeout(r, delay));
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser) {
          const { data: prof } = await supabase
            .from('profiles')
            .select('id')
            .eq('user_id', authUser.id)
            .maybeSingle();
          if (prof) { profileFound = true; break; }
        }
        delay = Math.min(delay * 2, 2000);
      }

      if (!profileFound) {
        setError(t('Account created but profile setup is taking longer than expected. Please refresh and log in.'));
        setLoading(false);
        return;
      }

      if (isHouseholdInvite) {
        try {
          const { error: acceptError } = await supabase.rpc('accept_household_invite');
          if (acceptError) console.warn('Household invite acceptance failed:', acceptError.message);

          const profileUpdate: Record<string, string | null> = {};
          const formattedSms = formatPhone(phoneSms);
          const formattedWa = formatPhone(samePhone ? phoneSms : phoneWhatsapp);
          if (formattedSms) profileUpdate.phone_sms = formattedSms;
          if (formattedWa) profileUpdate.phone_whatsapp = formattedWa;
          if (selectedCity) {
            profileUpdate.city = selectedCity;
            profileUpdate.timezone = resolveTimezone(selectedCity) || detectBrowserTimezone();
          }
          if (Object.keys(profileUpdate).length > 0) {
            const { data: { user: authUser } } = await supabase.auth.getUser();
            if (authUser) {
              await supabase.from('profiles').update(profileUpdate).eq('user_id', authUser.id);
            }
          }
          await refetchProfile();
        } catch (err) {
          console.warn('Error accepting household invite:', err);
        }
      }

      setLoginSuccess(true);
      return;
    }

    setLoading(true);
    const result = await signIn(email, password);
    
    if (result.success) {
      if (isHouseholdInvite) {
        try {
          await supabase.rpc('accept_household_invite');
          await refetchProfile();
        } catch (err) {
          console.warn('Error accepting household invite on login:', err);
        }
      }
      setLoginSuccess(true);
    } else {
      setError(result.error || t('Sign in failed'));
      setLoading(false);
    }
  };

  const inputClasses = "w-full pl-12 pr-4 py-3 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:ring-2 focus:ring-[#1A1A1A]/30 focus:border-[#1A1A1A]/40 transition-all";
  const labelClasses = "block text-sm font-medium text-[#636366] mb-2";
  const iconClasses = "absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8E8E93]";

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Language selector — pinned to top-right */}
      <div className="flex justify-end p-4">
        <LanguageSelector variant="light" />
      </div>

      {/* Centered login card */}
      <div className="flex-1 flex items-center justify-center px-4 pb-4">
      <div className="w-full max-w-md">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img 
              src="/turn-2.png" 
              alt="Turn Literacy Logo" 
              className="h-16 w-auto object-contain"
            />
          </div>
          {mode === 'signup' && isHouseholdInvite ? (
            <p className="text-[#636366] text-lg">{t("Create your account to join the family")}</p>
          ) : (
            <p className="text-[#636366] text-lg">{t("Please login to access your child's enrichment modules")}</p>
          )}
        </div>

        {/* Login Card */}
        <div className="glass-elevated rounded-3xl p-8">
          {/* Error Message */}
          {(error || authError) && (
            <div className="flex items-center gap-2 p-4 bg-[#FF3B30]/8 border border-[#FF3B30]/15 rounded-xl text-[#FF3B30] mb-6">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error || authError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Field (signup only) */}
            {mode === 'signup' && (
              <div>
                <label htmlFor="signup-name" className={labelClasses}>
                  {t('Your Name')}
                </label>
                <div className="relative">
                  <UserPlus className={iconClasses} />
                  <input
                    id="signup-name"
                    type="text"
                    value={signupName}
                    onChange={(e) => setSignupName(e.target.value)}
                    placeholder={t('First and last name')}
                    required
                    className={inputClasses}
                  />
                </div>
              </div>
            )}

            {/* City Field (household invite signup only) */}
            {mode === 'signup' && isHouseholdInvite && (
              <div className="relative" ref={cityDropdownRef}>
                <label htmlFor="signup-city" className={labelClasses}>
                  {t('City')}
                </label>
                <div className="relative">
                  <MapPin className={iconClasses} />
                  <input
                    id="signup-city"
                    type="text"
                    value={citySearch}
                    onChange={(e) => {
                      setCitySearch(e.target.value);
                      setShowCityDropdown(true);
                      const match = findCityByLabel(e.target.value);
                      setSelectedCity(match ? match.value : e.target.value.trim());
                    }}
                    onFocus={() => setShowCityDropdown(true)}
                    onBlur={() => setTimeout(() => setShowCityDropdown(false), 150)}
                    placeholder={t('Start typing your city...')}
                    autoComplete="off"
                    className={inputClasses}
                  />
                </div>
                {showCityDropdown && filteredCities.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-xl glass-elevated shadow-lg">
                    {filteredCities.map((city) => (
                      <button
                        key={city.value}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setCitySearch(city.label);
                          setSelectedCity(city.value);
                          setShowCityDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-[#1C1C1E] hover:bg-black/[0.04] transition-colors"
                      >
                        {getCityWithTimezone(city.value)}
                      </button>
                    ))}
                  </div>
                )}
                {resolvedTzLabel && (
                  <p className="text-xs text-[#8E8E93] mt-1">{t('Time Zone')}: {resolvedTzLabel}</p>
                )}
              </div>
            )}

            {/* Phone SMS Field (household invite signup only) */}
            {mode === 'signup' && isHouseholdInvite && (
              <div>
                <label htmlFor="signup-phone" className={labelClasses}>
                  {t('Phone (SMS)')}
                </label>
                <div className="relative">
                  <Phone className={iconClasses} />
                  <input
                    id="signup-phone"
                    type="tel"
                    value={phoneSms}
                    onChange={(e) => setPhoneSms(e.target.value)}
                    placeholder={phonePlaceholder}
                    className={inputClasses}
                  />
                </div>
                <label className="flex items-center gap-2 mt-2 text-sm text-[#636366] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={samePhone}
                    onChange={(e) => setSamePhone(e.target.checked)}
                    className="rounded border-black/20 bg-black/[0.03] text-[#1A1A1A] focus:ring-[#1A1A1A]"
                  />
                  {t('Same number for WhatsApp')}
                </label>
                {!samePhone && (
                  <div className="relative mt-2">
                    <Phone className={iconClasses} />
                    <input
                      id="signup-whatsapp"
                      type="tel"
                      value={phoneWhatsapp}
                      onChange={(e) => setPhoneWhatsapp(e.target.value)}
                      placeholder={`WhatsApp: ${phonePlaceholder}`}
                      className={inputClasses}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="login-email" className={labelClasses}>
                {t('Email Address')}
              </label>
              <div className="relative">
                <Mail className={iconClasses} />
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  readOnly={mode === 'signup' && isHouseholdInvite && !!inviteEmail}
                  className={`${inputClasses} ${mode === 'signup' && isHouseholdInvite && inviteEmail ? 'opacity-60' : ''}`}
                />
              </div>
              {mode === 'signup' && isHouseholdInvite && inviteEmail && (
                <p className="text-xs text-[#8E8E93] mt-1">{t('Use this email to link to the family account')}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="login-password" className={labelClasses}>
                {t('Password')}
              </label>
              <div className="relative">
                <Lock className={iconClasses} />
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full pl-12 pr-12 py-3 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:ring-2 focus:ring-[#1A1A1A]/30 focus:border-[#1A1A1A]/40 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8E8E93] hover:text-[#636366]"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password (signup only) */}
            {mode === 'signup' && (
              <div>
                <label htmlFor="confirm-password" className={labelClasses}>
                  {t('Confirm Password')}
                </label>
                <div className="relative">
                  <Lock className={iconClasses} />
                  <input
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className={inputClasses}
                  />
                </div>
              </div>
            )}

            {/* Forgot Password Link (login only) */}
            {mode === 'login' && (
              <div className="flex justify-end">
                <Link
                  to="/reset-password"
                  className="text-sm text-[#1A1A1A] hover:text-[#4A2476] transition-colors"
                >
                  {t('Forgot password?')}
                </Link>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || authLoading}
              className="w-full py-3 px-4 bg-[#111111] text-white rounded-xl font-semibold hover:bg-[#333333] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {(loading || authLoading) ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {mode === 'signup' ? t('Creating account...') : t('Logging in...')}
                </>
              ) : (
                mode === 'signup' ? t('Create Account') : t('Login')
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-[#8E8E93] mt-6">
          {mode === 'signup' ? (
            <>
              {t('Already have an account?')}{' '}
              <button
                onClick={() => { setMode('login'); setError(null); }}
                className="text-[#1A1A1A] hover:text-[#4A2476] transition-colors"
              >
                {t('Log in')}
              </button>
            </>
          ) : (
            <>
              {isHouseholdInvite ? (
                <>
                  {t("Don't have an account?")}{' '}
                  <button
                    onClick={() => { setMode('signup'); setError(null); }}
                    className="text-[#1A1A1A] hover:text-[#4A2476] transition-colors"
                  >
                    {t('Sign up')}
                  </button>
                </>
              ) : (
                <>
                  {t("Don't have an account?")}{' '}
                  <a href="https://www.turneducation.com/start" className="text-[#1A1A1A] hover:text-[#333] transition-colors">
                    {t('Get Started')}
                  </a>
                </>
              )}
            </>
          )}
        </p>
      </div>
      </div>
    </div>
  );
};

export default Login;
